import { DoorHangerLinkSettings } from './DoorHangers.tsx';
import { Fragment, useEffect, useState } from 'react';
import { DEFAULT_STATUS_LABELS, PERMISSION_INFO, permissionsFor } from '@civilos/domain';
import { api, fileToBase64 } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { isBlueHQ } from '../shared/site.ts';
import { fmtDateTime } from '../shared/format.ts';
import { Badge, Card, ErrorBox, Field, Notice, Spinner, useAction, useLoad, RowMenu } from '../shared/ui.tsx';
import { ScanAISettings } from './ScanAI.tsx';
import { RulesEditor } from './RulesEditor.tsx';

type Tab = 'users' | 'directory' | 'rules' | 'settings' | 'devices';

export function Admin() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('users');
  const tabs: [Tab, string, boolean][] = [
    ['users', 'Users', can('admin.users')],
    ['directory', 'Courts & customers', can('admin.config')],
    ['rules', 'Rules & paper types', can('admin.rules')],
    ['settings', 'Agency settings', can('admin.config')],
    ['devices', 'Devices', can('admin.users')],
  ];
  return (
    <div className="stack">
      <h1>Administration</h1>
      <div className="tabs">
        {tabs.filter((t) => t[2]).map(([k, l]) => (
          <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
            {l}
          </button>
        ))}
      </div>
      {tab === 'users' && <Users />}
      {tab === 'directory' && <Directory />}
      {tab === 'rules' && <Rules />}
      {tab === 'settings' && (
        <>
          <Settings />
          <AddressPoints />
          <DoorHangerLinkSettings />
        </>
      )}
      {tab === 'devices' && <Devices />}
    </div>
  );
}

/** Invite by email: the person picks their own username and password. */
function Invites({ meta, onChanged }: { meta: any; onChanged: () => void }) {
  const inv = useLoad(() => api.get<any>('/api/admin/invites'));
  const a = useAction();
  const blank = { email: '', displayName: '', role: 'process_server', title: '', badgeNumber: '', customerId: '' };
  const [f, setF] = useState(blank);
  const [sent, setSent] = useState<{ to: string; link: string } | null>(null);
  const reload = () => (inv.reload(), onChanged());
  return (
    <Card title="Invite someone">
      <p className="small muted">They get an email with a link (good for 7 days) to pick their own username and password. People whose role needs two-factor set up their authenticator the first time they sign in.</p>
      <form
        className="grid2"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const r = await api.post<{ link: string }>('/api/admin/invites', { ...f, title: f.title || undefined, badgeNumber: f.badgeNumber || undefined, customerId: f.role === 'customer' ? f.customerId : undefined });
            setSent({ to: f.email, link: r.link });
            setF(blank);
            reload();
          });
        }}
      >
        <Field label="Name"><input value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} required aria-label="Invite name" /></Field>
        <Field label="Email"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required aria-label="Invite email" /></Field>
        <Field label="Role">
          <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} aria-label="Invite role">
            {Object.entries(meta?.roles ?? {}).filter(([k]) => k !== 'super_admin').map(([k, v]) => <option key={k} value={k}>{String(v)}</option>)}
          </select>
        </Field>
        {f.role === 'customer' ? (
          <Field label="Portal account for">
            <select value={f.customerId} onChange={(e) => setF({ ...f, customerId: e.target.value })} required>
              <option value="">Choose…</option>
              {(meta?.customers ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
        ) : (
          <Field label="Badge # (optional)"><input value={f.badgeNumber} onChange={(e) => setF({ ...f, badgeNumber: e.target.value })} /></Field>
        )}
        <Field label="Title (optional)"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Deputy Sheriff" /></Field>
        <div className="row"><button className="btn primary" disabled={a.busy}>Send invitation</button></div>
      </form>
      {sent && <InviteSent to={sent.to} link={sent.link} />}
      <ErrorBox error={a.error} />
      {(inv.data?.invites ?? []).length > 0 && (
        <>
          <h3 className="small-h">Waiting for them to accept</h3>
          <table className="table small">
            <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Sent</th><th /></tr></thead>
            <tbody>
              {inv.data.invites.map((i: any) => (
                <tr key={i.id}>
                  <td>{i.display_name}</td>
                  <td>{i.email}</td>
                  <td>{meta?.roles?.[i.role] ?? i.role}</td>
                  <td>{new Date(i.created_at).toLocaleDateString()} {i.expired ? <Badge tone="bad">expired</Badge> : null}</td>
                  <td className="row gap-sm">
                    <button className="btn sm" onClick={() => a.run(async () => { const r = await api.post<{ link: string }>(`/api/admin/invites/${i.id}/resend`); setSent({ to: i.email, link: r.link }); inv.reload(); })}>Resend</button>
                    <button className="btn sm ghost" onClick={() => window.confirm(`Withdraw the invitation to ${i.email}?`) && a.run(async () => { await api.post(`/api/admin/invites/${i.id}/revoke`); inv.reload(); })}>Withdraw</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Card>
  );
}

/** After sending: the link, for when email isn't connected yet or lands in spam. */
export function InviteSent({ to, link }: { to: string; link: string }) {
  const url = `${location.origin}${link}`;
  return (
    <div className="link-box">
      <div className="small">✓ Invitation emailed to <strong>{to}</strong>. If it doesn’t arrive, send them this link (works for 7 days):</div>
      <div className="row gap-sm wrap">
        <input readOnly value={url} className="grow mono" aria-label="Invitation link" onFocus={(e) => e.target.select()} />
        <button type="button" className="btn sm" onClick={() => navigator.clipboard?.writeText(url)}>Copy link</button>
      </div>
    </div>
  );
}

const mdy = (iso: string | null) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
};

/** Each notary's commission as they reported it on My profile. */
function Notaries() {
  const r = useLoad(() => api.get<{ notaries: any[] }>('/api/admin/notaries'));
  return (
    <Card title="Notaries">
      {!r.data ? (
        <ErrorBox error={r.error} />
      ) : r.data.notaries.length === 0 ? (
        <p className="small muted">No one has entered a notary commission yet. Notaries enter theirs on My profile.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Full name on commission</th><th>User</th><th>Commissioned</th><th>Expires</th><th>Status</th></tr></thead>
            <tbody>
              {r.data.notaries.map((n) => (
                <tr key={n.userId}>
                  <td><strong>{n.name}</strong></td>
                  <td>{n.displayName}</td>
                  <td>{mdy(n.commissioned) ?? <Badge tone="warn">missing</Badge>}</td>
                  <td>{mdy(n.expires)}</td>
                  <td>
                    {n.daysLeft < 0 ? <Badge tone="bad">expired</Badge> : n.daysLeft <= 30 ? <Badge tone="warn">{n.daysLeft} days left</Badge> : <Badge tone="ok">current</Badge>}
                    {!n.hasSignature && <> <Badge tone="warn">no saved signature</Badge></>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="small muted">Each notary keeps their own commission up to date on My profile. CivilHound won’t notarize with an expired or incomplete commission.</p>
    </Card>
  );
}

const PERM_GROUPS = (() => {
  const g = new Map<string, [string, string][]>();
  for (const [k, v] of Object.entries(PERMISSION_INFO)) g.set(v.group, [...(g.get(v.group) ?? []), [k, v.label]]);
  return [...g];
})();

/** Every permission as a checkbox, grouped. `marks` notes which ones differ from the starting set. */
function PermGrid({ on, setOn, base, rms }: { on: Set<string>; setOn: (s: Set<string>) => void; base?: Set<string>; rms: boolean }) {
  const flip = (k: string) => {
    const n = new Set(on);
    if (n.has(k)) n.delete(k);
    else n.add(k);
    setOn(n);
  };
  return (
    <div className="perm-grid">
      {PERM_GROUPS.filter(([g]) => rms || g === 'Civil process' || g === 'Administration' || g === 'People & agency').map(([g, list]) => (
        <fieldset key={g} className="perm-group">
          <legend>
            {g}{' '}
            <button type="button" className="btn link small" onClick={() => setOn(new Set([...on, ...list.map(([k]) => k)]))}>
              all
            </button>{' '}
            <button type="button" className="btn link small" onClick={() => setOn(new Set([...on].filter((k) => !list.some(([x]) => x === k))))}>
              none
            </button>
          </legend>
          {list.map(([k, l]) => {
            const changed = base && base.has(k) !== on.has(k);
            return (
              <label key={k} className={`check${changed ? ' perm-changed' : ''}`}>
                <input type="checkbox" checked={on.has(k)} onChange={() => flip(k)} /> {l}
                {changed && <span className="small"> {on.has(k) ? '(added)' : '(taken away)'}</span>}
              </label>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}

/** One person's access: their profile (or role defaults), plus checkboxes to add or take away anything. */
function AccessEditor({ u, profiles, roleName, onSave, onClose }: { u: any; profiles: any[]; roleName: string; onSave: (b: object) => void; onClose: () => void }) {
  const rms = isBlueHQ();
  const [profile, setProfile] = useState<string>(u.access_profile ?? '');
  const baseFor = (pid: string) => new Set<string>(pid ? profiles.find((p) => p.id === pid)?.permissions ?? [] : [...permissionsFor(u.role, [])].filter((k) => k !== 'portal.use'));
  const start = () => {
    const b = baseFor(u.access_profile ?? '');
    for (const k of u.extra_permissions ?? []) b.add(k);
    for (const k of u.denied_permissions ?? []) b.delete(k);
    return b;
  };
  const [on, setOn] = useState<Set<string>>(start);
  const base = baseFor(profile);
  const pick = (pid: string) => (setProfile(pid), setOn(baseFor(pid)));
  const save = () => onSave({ accessProfile: profile || null, extraPermissions: [...on].filter((k) => !base.has(k)), deniedPermissions: [...base].filter((k) => !on.has(k)) });
  return (
    <tr className="extra-row">
      <td colSpan={7}>
        <div className="extra-box stack sm">
          <strong>Access for {u.display_name}</strong>
          {u.role === 'agency_admin' ? (
            <p className="muted small">Agency admins always have everything, so the agency can’t be locked out.</p>
          ) : (
            <>
              <label className="row gap-sm" style={{ alignItems: 'center' }}>
                Profile
                <select value={profile} onChange={(e) => pick(e.target.value)} style={{ maxWidth: 320 }}>
                  <option value="">{roleName} (role defaults)</option>
                  {profiles.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <p className="small muted">Start from a profile, then check or uncheck anything for this one person. Changes from the profile are marked.</p>
              <PermGrid on={on} setOn={setOn} base={base} rms={rms} />
            </>
          )}
          <div className="row gap-sm">
            {u.role !== 'agency_admin' && (
              <button className="btn primary sm" onClick={save}>
                Save
              </button>
            )}
            <button className="btn ghost sm" onClick={onClose}>
              {u.role === 'agency_admin' ? 'Close' : 'Cancel'}
            </button>
          </div>
        </div>
      </td>
    </tr>
  );
}

/** The agency's access profiles: named sets of checkboxes ("Patrol deputy", "Investigator"…) to give people. */
function AccessProfiles({ data, roles, onChanged }: { data: any; roles: Record<string, string>; onChanged: () => void }) {
  const rms = isBlueHQ();
  const [edit, setEdit] = useState<{ id?: string; name: string; on: Set<string> } | null>(null);
  const [from, setFrom] = useState('process_server');
  const a = useAction();
  return (
    <Card title="Access profiles" actions={!edit && <button className="btn sm" onClick={() => setEdit({ name: '', on: new Set(data.roleDefaults[from] ?? []) })}>New profile</button>}>
      <p className="small muted">A profile is a set of checkboxes you can give to many people (for example “Patrol deputy” or “Investigator”). Change the profile and everyone on it changes. You can still add or take away access for one person from their Access button.</p>
      {!data.profiles.length && !edit && <p className="muted small">No profiles yet — everyone gets their role’s defaults.</p>}
      {!edit && data.profiles.length > 0 && (
        <ul className="plain">
          {data.profiles.map((p: any) => (
            <li key={p.id} className="row gap-sm wrap" style={{ padding: '6px 0', borderBottom: '1px solid var(--line-2)' }}>
              <strong style={{ flex: 1 }}>{p.name}</strong>
              <span className="small muted">
                {p.permissions.length} permissions · {p.users} {p.users === 1 ? 'person' : 'people'}
              </span>
              <button className="btn sm" onClick={() => setEdit({ id: p.id, name: p.name, on: new Set(p.permissions) })}>
                Edit
              </button>
              <button className="btn sm ghost" disabled={a.busy} onClick={() => window.confirm(`Delete “${p.name}”? The ${p.users} people on it go back to their role’s defaults.`) && a.run(async () => (await api.post(`/api/admin/access-profiles/${p.id}/delete`), onChanged()))}>
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
      {edit && (
        <div className="stack sm">
          <div className="row gap-sm wrap" style={{ alignItems: 'end' }}>
            <label style={{ flex: 1, minWidth: 220 }}>
              Profile name
              <input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="e.g. Patrol deputy" />
            </label>
            {!edit.id && (
              <label>
                Start from
                <select value={from} onChange={(e) => (setFrom(e.target.value), setEdit({ ...edit, on: new Set(data.roleDefaults[e.target.value] ?? []) }))}>
                  {Object.keys(data.roleDefaults).filter((r) => r !== 'customer').map((r) => (
                    <option key={r} value={r}>
                      {roles[r] ?? r}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
          <PermGrid on={edit.on} setOn={(on) => setEdit({ ...edit, on })} rms={rms} />
          <ErrorBox error={a.error} />
          <div className="row gap-sm">
            <button className="btn primary sm" disabled={a.busy || !edit.name.trim()} onClick={() => a.run(async () => (await api.post('/api/admin/access-profiles', { id: edit.id, name: edit.name, permissions: [...edit.on] }), setEdit(null), onChanged()))}>
              Save profile
            </button>
            <button className="btn ghost sm" onClick={() => setEdit(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

function Users() {
  const r = useLoad(() => api.get('/api/admin/users'));
  const meta = useLoad(() => api.get('/api/meta'));
  const a = useAction();
  const [f, setF] = useState({ username: '', displayName: '', role: 'process_server', title: '', badgeNumber: '', email: '', password: '', customerId: '', civilAccess: isBlueHQ() ? 'lookup' : '' });
  const rms = isBlueHQ();
  const upd = (id: string, body: object) => a.run(async () => { await api.patch(`/api/admin/users/${id}`, body); r.reload(); });
  const [extra, setExtra] = useState<string | null>(null);
  const prof = useLoad(() => api.get<any>('/api/admin/access-profiles'));
  const profName = (id: string | null) => (id ? prof.data?.profiles.find((p: any) => p.id === id)?.name : null);
  return (
    <>
      <Card title="Users">
        <ErrorBox error={a.error} />
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Name</th><th>Username</th><th>Role</th><th>2FA</th><th>Fingerprint</th><th>Status</th><th /></tr></thead>
            <tbody>
              {r.data?.users.map((u: any) => (
                <Fragment key={u.id}>
                <tr>
                  <td>{u.display_name} {u.badge_number && <span className="muted">({u.badge_number})</span>}</td>
                  <td className="mono">{u.username}</td>
                  <td>
                    {meta.data?.roles[u.role] ?? u.role}
                    {profName(u.access_profile) && <div className="small">Profile: <strong>{profName(u.access_profile)}</strong></div>}
                    {((u.extra_permissions ?? []).length > 0 || (u.denied_permissions ?? []).length > 0) && (
                      <div className="small muted">
                        {(u.extra_permissions as string[]).map((k) => `+ ${(PERMISSION_INFO as any)[k]?.label ?? k}`).concat((u.denied_permissions ?? []).map((k: string) => `− ${(PERMISSION_INFO as any)[k]?.label ?? k}`)).join(', ')}
                      </div>
                    )}
                    {rms && u.role === 'process_server' && u.civil_access && <div className="small">{u.civil_access === 'full' ? <Badge tone="info">Civil deputy</Badge> : <Badge tone="neutral">Patrol · civil look-up only</Badge>}</div>}
                  </td>
                  <td>{u.totp_enabled ? <Badge tone="ok" dot>On</Badge> : <span className="muted">Off</span>}</td>
                  <td>{u.fingerprints ? <Badge tone="ok">{u.fingerprints === 1 ? '1 device' : `${u.fingerprints} devices`}</Badge> : <span className="muted small">—</span>}</td>
                  <td>{u.active ? <Badge tone="ok" dot>Active</Badge> : <Badge tone="bad" dot>Deactivated</Badge>}</td>
                  <td className="num">
                    <RowMenu
                      items={[
                        { label: 'Reset password…', onClick: () => { const pw = window.prompt('Temporary password (12+ chars, letters & numbers). User must change it at next login.'); if (pw) upd(u.id, { resetPassword: pw }); } },
                        !!(u.email && u.active) && { label: 'Email a reset link', onClick: () => a.run(async () => { const x = await api.post<{ sentTo: string }>(`/api/admin/users/${u.id}/send-reset`); window.alert(`Reset link emailed to ${x.sentTo}. It works for 1 hour.`); }) },
                        !!u.totp_enabled && { label: 'Reset authenticator…', onClick: () => window.confirm('Reset this user’s authenticator?') && upd(u.id, { resetTwoFactor: true }) },
                        u.role !== 'customer' && { label: 'Access (checkboxes)…', onClick: () => setExtra(u.id) },
                        rms && u.role === 'process_server' && u.civil_access !== 'full' && { label: 'Make civil deputy (serves civil papers)', onClick: () => upd(u.id, { civilAccess: 'full' }) },
                        rms && u.role === 'process_server' && u.civil_access !== 'lookup' && { label: 'Make patrol deputy (civil look-up only)', onClick: () => upd(u.id, { civilAccess: 'lookup' }) },
                        { label: u.active ? 'Deactivate' : 'Reactivate', danger: u.active, onClick: () => upd(u.id, { active: !u.active }) },
                      ]}
                    />
                  </td>
                </tr>
                {extra === u.id && <AccessEditor u={u} profiles={prof.data?.profiles ?? []} roleName={meta.data?.roles[u.role] ?? u.role} onClose={() => setExtra(null)} onSave={(body) => (upd(u.id, body), setExtra(null))} />}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="small muted">Deactivated users keep their history, cannot sign in or receive work, and any papers in their custody appear in Needs Attention for reconciliation.</p>
      </Card>
      {prof.data && <AccessProfiles data={prof.data} roles={meta.data?.roles ?? {}} onChanged={() => (prof.reload(), r.reload())} />}
      <Notaries />
      <Invites meta={meta.data} onChanged={r.reload} />
      <Card title="Add user with a temporary password">
        <form className="grid2" onSubmit={(e) => { e.preventDefault(); a.run(async () => { await api.post('/api/admin/users', { ...f, customerId: f.role === 'customer' || f.role === 'magistrate' ? f.customerId || undefined : undefined, civilAccess: f.role === 'process_server' && f.civilAccess ? f.civilAccess : undefined }); setF({ ...f, username: '', displayName: '', password: '' }); r.reload(); }); }}>
          <Field label="Display name"><input value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} required /></Field>
          <Field label="Username"><input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} required /></Field>
          <Field label="Role">
            <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              {Object.entries(meta.data?.roles ?? {}).filter(([k]) => k !== 'super_admin').map(([k, v]) => <option key={k} value={k}>{String(v)}</option>)}
            </select>
          </Field>
          {(f.role === 'customer' || f.role === 'magistrate') && (
            <Field label={f.role === 'magistrate' ? 'Their court (civil papers they see)' : 'Portal account for'} hint={f.role === 'magistrate' ? 'Leave blank if the agency has only one magistrate court account' : 'The law office or court whose papers this login can see'}>
              <select value={f.customerId} onChange={(e) => setF({ ...f, customerId: e.target.value })} required={f.role === 'customer'}>
                <option value="">Choose…</option>
                {(meta.data?.customers ?? []).map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
          )}
          {rms && f.role === 'process_server' && (
            <Field label="Civil papers" hint="Civil deputies are assigned and serve civil papers. Patrol deputies can only look up active civil papers and evictions.">
              <select value={f.civilAccess} onChange={(e) => setF({ ...f, civilAccess: e.target.value })}>
                <option value="lookup">Patrol deputy — look up only</option>
                <option value="full">Civil deputy — serves civil papers</option>
                <option value="">Not set (serves once papers are assigned)</option>
              </select>
            </Field>
          )}
          <Field label="Title"><input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label="Badge #"><input value={f.badgeNumber} onChange={(e) => setF({ ...f, badgeNumber: e.target.value })} /></Field>
          <Field label="Email"><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></Field>
          <Field label="Temporary password" hint="12+ characters with letters and numbers"><input value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required minLength={12} /></Field>
          <div className="row"><button className="btn primary" disabled={a.busy}>Create user</button></div>
        </form>
      </Card>
    </>
  );
}

function Directory() {
  const courts = useLoad(() => api.get('/api/courts'));
  const customers = useLoad(() => api.get('/api/customers'));
  const a = useAction();
  const [c, setC] = useState({ name: '', courtType: 'magistrate', county: '' });
  const [cu, setCu] = useState({ name: '', customerClass: 'attorney', email: '', phone: '' });
  return (
    <div className="grid2 top">
      <Card title="Courts">
        <ul className="plain">
          {courts.data?.courts.map((x: any) => (
            <li key={x.id} className="row between">
              <span>{x.name} <span className="muted small">{x.court_type}</span></span>
              <button className="btn sm ghost" onClick={() => a.run(async () => { await api.post(`/api/courts/${x.id}/archive`); courts.reload(); })}>Archive</button>
            </li>
          ))}
        </ul>
        <form className="stack" onSubmit={(e) => { e.preventDefault(); a.run(async () => { await api.post('/api/courts', c); setC({ ...c, name: '' }); courts.reload(); }); }}>
          <Field label="Court name"><input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} required /></Field>
          <Field label="Type" hint="Family court papers are left out of the Writ Book">
            <select value={c.courtType} onChange={(e) => setC({ ...c, courtType: e.target.value })}>
              <option value="magistrate">Magistrate</option>
              <option value="common_pleas">Common Pleas</option>
              <option value="family">Family Court</option>
              <option value="probate">Probate</option>
              <option value="circuit">Circuit / General Sessions</option>
              <option value="other">Other</option>
            </select>
          </Field>
          <button className="btn">Add court</button>
        </form>
      </Card>
      <Card title="Customers">
        <ul className="plain">
          {customers.data?.customers.map((x: any) => (
            <li key={x.id} className="row between">
              <span>{x.name} <Badge>{x.customer_class}</Badge></span>
              <button className="btn sm ghost" onClick={() => a.run(async () => { await api.post(`/api/customers/${x.id}/archive`); customers.reload(); })}>Archive</button>
            </li>
          ))}
        </ul>
        <form className="stack" onSubmit={(e) => { e.preventDefault(); a.run(async () => { await api.post('/api/customers', cu); setCu({ ...cu, name: '' }); customers.reload(); }); }}>
          <Field label="Name"><input value={cu.name} onChange={(e) => setCu({ ...cu, name: e.target.value })} required /></Field>
          <Field label="Class" hint="Drives the fee schedule">
            <select value={cu.customerClass} onChange={(e) => setCu({ ...cu, customerClass: e.target.value })}>
              {['attorney', 'magistrate', 'court', 'government', 'private'].map((k) => <option key={k}>{k}</option>)}
            </select>
          </Field>
          <Field label="Email"><input value={cu.email} onChange={(e) => setCu({ ...cu, email: e.target.value })} /></Field>
          <button className="btn">Add customer</button>
        </form>
      </Card>
      <ErrorBox error={a.error} />
    </div>
  );
}

function Rules() {
  return <RulesEditor />;
}

/** County address points: import the 911 address layer so papers land on the right house and house numbers show. */
function AddressPoints() {
  const r = useLoad(() => api.get<any>('/api/admin/address-points'));
  const a = useAction();
  const [url, setUrl] = useState('');
  const [result, setResult] = useState<any>(null);
  const run = (body: Record<string, unknown>, file?: File) =>
    a.run(async () => {
      if (file) body.fileBase64 = await fileToBase64(file);
      setResult(await api.post('/api/admin/address-points/import', body));
      r.reload();
    });
  const last = r.data?.lastImport;
  return (
    <Card title="County address points">
      <p className="small muted">
        The county’s 911 address point layer has a point on every house. CivilHound uses it to put papers on the right house (before any outside lookup) and shows house numbers when you zoom in on a map. Ask county GIS for the address point layer’s ArcGIS REST address, or upload the shapefile as they send it (the .zip), or a GeoJSON or CSV export.
      </p>
      {r.data && (
        <p>
          <strong>{r.data.points.toLocaleString()}</strong> address points loaded
          {last && (
            <span className="small muted">
              {' '}
              · last import {fmtDateTime(last.imported_at)} by {last.imported_by} from {last.source}
            </span>
          )}
        </p>
      )}
      <form
        className="row gap wrap"
        onSubmit={(e) => {
          e.preventDefault();
          run({ url });
        }}
      >
        <Field label="ArcGIS layer address" hint="Ends in /FeatureServer/0 or /MapServer/0. The layer has to be public.">
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…/arcgis/rest/services/AddressPoints/FeatureServer/0" style={{ minWidth: 380 }} />
        </Field>
        <button className="btn primary" disabled={a.busy || !url.trim()}>
          {a.busy ? 'Importing…' : 'Import from county GIS'}
        </button>
      </form>
      <label className="btn" style={{ alignSelf: 'flex-start' }}>
        Upload a file (zipped shapefile, GeoJSON or CSV)
        <input
          type="file"
          accept=".zip,.geojson,.json,.csv,.txt"
          hidden
          aria-label="Address points file"
          onChange={(e) => {
            const f = e.target.files?.[0];
            e.target.value = '';
            if (f) run({ filename: f.name, fileBase64: '' }, f);
          }}
        />
      </label>
      {result && (
        <Notice tone="ok">
          Imported {result.points.toLocaleString()} address points{result.skipped ? ` (${result.skipped.toLocaleString()} skipped — no house number, street or location)` : ''}. {result.papersMoved} open paper{result.papersMoved === 1 ? '' : 's'} moved onto the house.
        </Notice>
      )}
      <p className="small muted">Importing again replaces the old points. Papers a clerk pinned by hand aren’t moved.</p>
      <ErrorBox error={a.error} />
    </Card>
  );
}

function Settings() {
  const r = useLoad(() => api.get('/api/admin/settings'));
  const a = useAction();
  const [s, setS] = useState<any>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (r.data) setS({ ...r.data.settings, statusLabels: { ...(r.data.settings.statusLabels ?? {}) } });
  }, [r.data]);
  if (!s) return <Spinner />;
  const set = (k: string) => (e: any) => setS({ ...s, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  return (
    <Card title={`${r.data.name} (${r.data.code})`}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          a.run(async () => {
            const labels = Object.fromEntries(Object.entries(s.statusLabels ?? {}).map(([k, v]) => [k, String(v).trim()]).filter(([, v]) => v));
            const next: Record<string, unknown> = { ...s, autoLockMinutes: Number(s.autoLockMinutes) || 10, statusLabels: labels };
            // Send only what changed — stored settings managed elsewhere (webhooks, retired keys) aren't editable here.
            const orig = r.data.settings ?? {};
            const changed = Object.fromEntries(Object.entries(next).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(orig[k]) && !(v === '' && orig[k] == null)));
            if (Object.keys(changed).length) await api.patch('/api/admin/settings', { settings: changed });
            r.reload();
            setSaved(true);
          });
        }}
      >
        <div className="grid2">
          <Field label="Paper number prefix"><input value={s.paperNumberPrefix ?? ''} onChange={set('paperNumberPrefix')} /></Field>
          <Field label="Paper number format" hint="Tokens: {MM} {DD} {YY} {YYYY} {PREFIX} {SEQ}. With {DD} the count restarts each day (default {MM}{DD}{YY}-{SEQ})."><input value={s.paperNumberFormat ?? ''} onChange={set('paperNumberFormat')} /></Field>
          <Field label="County"><input value={s.countyName ?? ''} onChange={set('countyName')} /></Field>
          <Field label="Contact phone"><input value={s.contactPhone ?? ''} onChange={set('contactPhone')} /></Field>
          <Field label="Contact address"><input value={s.contactAddress ?? ''} onChange={set('contactAddress')} /></Field>
          <Field label="Barcode style">
            <select value={s.barcodeStyle ?? 'label'} onChange={set('barcodeStyle')}><option value="label">Barcode label</option><option value="cover_sheet">Printed cover sheet</option></select>
          </Field>
          <Field label="App auto-lock (minutes)"><input type="number" min={1} value={s.autoLockMinutes ?? 10} onChange={set('autoLockMinutes')} /></Field>
        </div>
        <h3>Deputy request forms (Resources page)</h3>
        <p className="small muted">Paste the Jotform (or other https) links. They appear as buttons on every deputy’s home page.</p>
        <div className="grid2">
          <Field label="Equipment request form link"><input type="url" placeholder="https://form.jotform.com/…" value={s.equipmentRequestUrl ?? ''} onChange={set('equipmentRequestUrl')} /></Field>
          <Field label="Vehicle repair request form link"><input type="url" placeholder="https://form.jotform.com/…" value={s.vehicleRepairUrl ?? ''} onChange={set('vehicleRepairUrl')} /></Field>
          <Field label="Notable / critical incident alert form link"><input type="url" placeholder="https://form.jotform.com/…" value={s.incidentAlertUrl ?? ''} onChange={set('incidentAlertUrl')} /></Field>
        </div>
        <label className="row gap-sm"><input type="checkbox" checked={s.custodyScanRequired !== false} onChange={set('custodyScanRequired')} /> Require barcode scans for custody changes</label>
        <label className="row gap-sm"><input type="checkbox" checked={!!s.requireTwoFactorForAll} onChange={set('requireTwoFactorForAll')} /> Require two-factor for all users</label>
        <label className="row gap-sm"><input type="checkbox" checked={!!s.requireFingerprintToSign} onChange={set('requireFingerprintToSign')} /> Require a fingerprint to sign and to notarize affidavits</label>
        <Card title="What each paper status is called">
          <p className="small muted">Leave a box blank to use the standard name.</p>
          <div className="grid2">
            {Object.entries(DEFAULT_STATUS_LABELS).map(([k, def]) => (
              <Field key={k} label={def}>
                <input aria-label={`Name for ${def}`} placeholder={def} value={s.statusLabels?.[k] ?? ''} onChange={(e) => setS({ ...s, statusLabels: { ...(s.statusLabels ?? {}), [k]: e.target.value } })} />
              </Field>
            ))}
          </div>
        </Card>
        <ScanAISettings s={s} set={set} />
        <Card title="Safety check-ins">
          <p className="small muted">While a deputy is on duty, no app activity for this long asks “Are you OK?” on their phone. Dispatch isn’t told anything until the deputy has gone the answer time without tapping “I’m OK” — then dispatch and supervisors are alerted. Moving alone doesn’t count as activity.</p>
          <div className="grid2">
            <Field label="Ask after (minutes with no activity)"><input type="number" min={1} max={480} value={s.inactivityMinutes ?? 30} onChange={set('inactivityMinutes')} /></Field>
            <Field label="Out of car: ask after (minutes)"><input type="number" min={1} max={480} value={s.onSceneMinutes ?? 15} onChange={set('onSceneMinutes')} /></Field>
            <Field label="Deputy has this long to answer before dispatch is alerted (minutes)"><input type="number" min={1} max={10} value={s.answerMinutes ?? 2} onChange={set('answerMinutes')} /></Field>
          </div>
        </Card>
        <Card title="Maps">
          <Field label="Map pictures" hint="Esri needs an ArcGIS Location Platform API key (location.arcgis.com). Restrict the key to this site’s address in Esri. If Esri tiles won’t load, maps fall back to the free ones.">
            <select value={s.mapProvider ?? (s.esriApiKey || r.data.platformEsriKey ? 'esri' : 'free')} onChange={set('mapProvider')}>
              <option value="free">Free — OpenStreetMap streets, USGS satellite</option>
              <option value="esri">Esri — ArcGIS streets and imagery</option>
            </select>
          </Field>
          {(s.mapProvider ?? (s.esriApiKey || r.data.platformEsriKey ? 'esri' : 'free')) === 'esri' && (
            <Field label="Esri API key" hint={r.data.platformEsriKey ? 'Leave blank to use the CivilHound key. Enter your county’s own key to use its Esri account instead.' : 'Paste the API key from location.arcgis.com → Developer credentials.'}>
              <input type="password" autoComplete="off" value={s.esriApiKey ?? ''} onChange={set('esriApiKey')} placeholder={r.data.platformEsriKey ? 'Using the CivilHound key' : 'AAPT…'} />
            </Field>
          )}
          <Field label="Address lookup (geocoding)" hint="Places new papers on the map. County address points (below) are used first. Otherwise only the street address is sent to the U.S. Census Bureau’s free geocoder — never names or case details.">
            <select value={s.geocoder ?? 'census'} onChange={set('geocoder')}>
              <option value="census">U.S. Census Bureau geocoder</option>
              <option value="off">Off — pins only from coordinates entered by staff</option>
            </select>
          </Field>
        </Card>
        <button className="btn primary" disabled={a.busy}>Save settings</button>
        {saved && <Notice tone="ok">Saved. Some changes apply after users refresh.</Notice>}
        <ErrorBox error={a.error} />
      </form>
    </Card>
  );
}

function Devices() {
  const r = useLoad(() => api.get('/api/admin/devices'));
  const a = useAction();
  return (
    <Card title="Registered devices">
      <p className="small muted">Remote wipe signs the user out everywhere and erases offline case data on the device’s next connection. Formal device approval is part of the mobile phase.</p>
      <table className="table">
        <thead><tr><th>Device</th><th>User</th><th>Last seen</th><th>Status</th><th /></tr></thead>
        <tbody>
          {r.data?.devices.map((d: any) => (
            <tr key={d.id}>
              <td className="mono small">{d.id}<div className="muted">{d.user_agent?.slice(0, 60)}</div></td>
              <td>{d.user_name}</td>
              <td>{fmtDateTime(d.last_seen)}</td>
              <td>{d.wipe_requested_at ? <Badge tone="bad">wipe requested</Badge> : <Badge tone="ok">active</Badge>}</td>
              <td>{!d.wipe_requested_at && <button className="btn sm warn" onClick={() => window.confirm('Sign out and wipe this device?') && a.run(async () => { await api.post(`/api/admin/devices/${encodeURIComponent(d.id)}/wipe`); r.reload(); })}>Wipe</button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <ErrorBox error={a.error} />
    </Card>
  );
}
